import { useCallback } from "react";
import type { BaseEntity, FieldValue, EditingState } from "../types";

interface UseCardEditingProps<TData extends BaseEntity> {
  data: TData[];
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  editValue: string;
  setEditingCell: (state: EditingState | null) => void;
  setEditValue: (value: string) => void;
}

export function useCardEditing<TData extends BaseEntity>({
  data,
  onInlineEdit,
  editValue,
  setEditingCell,
  setEditValue,
}: UseCardEditingProps<TData>) {
  const handleCellEdit = useCallback(
    (rowId: string, fieldId: string, currentValue: FieldValue) => {
      setEditingCell({ rowId, fieldId });
      setEditValue(String(currentValue || ""));
    },
    [setEditingCell, setEditValue]
  );

  const handleCellSave = useCallback(
    async (rowId: string, fieldId: string) => {
      if (onInlineEdit) {
        try {
          await onInlineEdit({
            id: rowId,
            [fieldId]: editValue,
          });
          setEditingCell(null);
          setEditValue("");
        } catch (error) {
          console.error("Error saving cell:", error);
        }
      }
    },
    [editValue, onInlineEdit, setEditingCell, setEditValue]
  );

  const handleCellCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue("");
  }, [setEditingCell, setEditValue]);

  return {
    handleCellEdit,
    handleCellSave,
    handleCellCancel,
  };
}
